import {
  DOCUMENT_SECURITY_STATUS,
  getExtensionFromBlob,
  MODE_CONSTANTS,
  MultiSelectFileInput,
  NAMESPACE,
} from '@ui-kit'
import { FieldValues, Path, RegisterOptions, PathValue } from 'react-hook-form'
import {
  elementHasPermission,
  getDisabledFromElement,
  getValidationsFromElement,
} from '../utils/form.utils'
import {
  componentNames,
  ENTITY_NAME,
  EXTRA_FIELDS,
  NAMESPACES,
} from '../utils/constants'
import { MultiUploadRenderProps } from '../types/formRenderer.types'
import { MULTI_UPLOAD_UTILS, STRINGS } from '../utils/strings'
import { handleFileChangeMultiUpload } from '../utils/elementRendererUtil'
import {
  addToaster,
  hideLoader,
  showLoader,
  splitCaseDocumentApi,
  getAxiosErrorMessage,
  isAxiosConflictError,
  CONFLICT_DOWNLOAD_FALLBACK,
  logAxiosError,
} from '@app-store'
import { uniqueId } from 'lodash'
import { FileDataFromExtraResource, ToasterTypeValues } from '../types/common'
import {
  deleteFileAttachment,
  fetchDownloadUrlForMultipleFIles,
  fetchDownloadUrlForSingleFIles,
  getEntityDataForUpload,
  rescanDocumentApi,
} from '../apis/fetchPresignedUrl.api'
import { AxiosProgressEvent } from 'axios'
import { useContext, useEffect, useState } from 'react'
import { RootState, useAppDispatch, useAppSelector } from '../utils/agent.store'
import { editEntityData } from '../apis/entityData.api'
import { isReadOnlyUser } from '../utils/permission'
import SplitFax from '../pages/intake-ques/SplitFax'
import { useParams } from 'react-router-dom'
import { QUEUE_STRINGS } from '../utils/queueIntake.types'
import { v4 as uuid } from 'uuid'
import { DataContext } from './listing-table/ListingTableFromLayout'
import { SplitObject } from '../types/intakeQueue.types'

const MultiUploadWrapper = <T extends FieldValues>({
  id,
  element,
  register,
  errors,
  setValue,
  mode,
  entityName,
  isOpenForListing,
  setRenderMultiUpload,
  tabIndex,
  fullLayout,
  dataId,
  onUpload,
  fromIntakeReview,
  fromQueueTable,
  customHeader,
  onEditClick,
  isEditButtonVisible,
  required,
  disabled,
}: MultiUploadRenderProps<T>) => {
  const searchParams = new URLSearchParams(window.location.search)
  const caseId = useParams().caseId || searchParams.get('caseId')
  const { refetchData } = useContext(DataContext)
  const [selectedFile, setSelectedFile] = useState<{
    index: number
    url: string
    content_type: string
    path?: string
    status?: string
  }>()
  const [isDownloading, setIsDownloading] = useState(false)
  const isUploadDisable = isReadOnlyUser() || !!disabled
  const hasPermission = elementHasPermission(element, mode)
  const isDisabled =
    !hasPermission || getDisabledFromElement(element) || !!disabled
  const [documentId, setDocumentId] = useState<number | null>(null)
  const [files, setFiles] = useState<File[]>([])
  const [faxUrl, setFaxUrl] = useState<string>('')
  const [refetctchCounter, setRefetchCounter] = useState(0)
  const dispatch = useAppDispatch()
  const handleToaster = (message: string, type: ToasterTypeValues) => {
    dispatch(
      addToaster({
        id: uniqueId(),
        message: message,
        type: type,
      })
    )
  }
  const [filesUploaded, setFilesUploaded] = useState<
    { id: number; _state: string }[]
  >([])
  const [fileSubResourceData, setFileSubResourceData] = useState<
    FileDataFromExtraResource[]
  >([])
  const [noDataFound, setNoDataFound] = useState(false)
  const [aePcDocId, setAePcDocId] = useState<number | null>(null)
  const entitiesByName = useAppSelector(
    (state: RootState) => state?.entity?.entitiesByName
  )
  const docEntityId = entitiesByName?.[ENTITY_NAME?.AE_PC_DOCUMENT]?.id
  const saveRecordForListing = async (document: number) => {
    if (
      isOpenForListing &&
      id === NAMESPACES.NO_DATA &&
      !documentId &&
      fullLayout
    ) {
      try {
        editEntityData(
          fullLayout?.entity_name,
          fullLayout?.entity_id,
          fullLayout?.id ?? 0,
          { [element.keyName + '_id']: document },
          dataId ?? 0
        )
        onUpload && onUpload(document)
      } catch (error) {
        console.warn(error)
      }
    }
  }
  const onChange = async (
    filesList: File[] | null,
    index?: number,
    onProgress?: (
      progressEvent: AxiosProgressEvent,
      index?: number | undefined
    ) => void
  ) => {
    if (filesList?.length && onProgress) {
      const existingDocId =
        documentId ?? (id && id !== NAMESPACES.NO_DATA ? Number(id) : null)

      const data = await handleFileChangeMultiUpload({
        files: filesList,
        field_id: element?.meta?.field_id ?? 0,
        entityId: element?.meta?.d_entity_id ?? 0,
        onProgress,
        documentId,
        handleToaster,
        mode,
        filesLength: files?.length,
      })

      if (!data) {
        setFiles((prev) =>
          prev.filter(
            (f) =>
              !filesList.some(
                (failedFile) =>
                  failedFile.name === f.name && failedFile.size === f.size
              )
          )
        )
        if (!existingDocId) {
          setDocumentId(null)
          setFileSubResourceData([])
          setNoDataFound(true)
          setValue &&
            setValue(
              element.keyName as Path<T>,
              undefined as PathValue<T, Path<T>>
            )
        }
        return
      }

      saveRecordForListing(data)
      setDocumentId(data)
      fetchDataForViewAndEdit(data)
      setValue &&
        setValue(
          element.keyName as Path<T>,
          { id: data ?? id } as PathValue<T, Path<T>>
        )
      if (data?.length) {
        if (filesUploaded?.length) {
          setFilesUploaded([...filesUploaded, ...data])
        }
      }
    } else if (index !== null && index !== undefined) {
      if (filesUploaded?.length) {
        const updatedData = filesUploaded?.filter((_, i) => i !== index)
        setFilesUploaded([...updatedData])
      }
    }
  }
  const fetchDataForViewAndEdit = async (newDocId?: number) => {
    const docId = newDocId ?? id
    if (docId && docId !== NAMESPACES.NO_DATA) {
      try {
        setNoDataFound(false)
        const { data } = await getEntityDataForUpload(
          STRINGS.MULTI_UPLOAD_FIELDS_AE_PC_REQUIRED,
          typeof docId === 'number' ? docId : +docId
        )
        if (data?.attachment?.length) {
          setFileSubResourceData(data?.attachment)
        } else setNoDataFound(true)
        if (data?.id) setDocumentId(data?.id)
        if (data?.c_ae_pc_document?.length && data.c_ae_pc_document[0]?.id) {
          setAePcDocId(data?.c_ae_pc_document[0]?.id)
        }
        return data
      } catch (error) {
        setNoDataFound(true)
        console.log(error)
      }
    }
  }
  const handleDownloadSingleFile = async (index: number, type?: string) => {
    if (!fileSubResourceData?.length || isDownloading) {
      return
    }
    const { path, name } = fileSubResourceData[index]
    setIsDownloading(true)
    try {
      const { data } = await fetchDownloadUrlForSingleFIles(path, name)
      if (!data) {
        throw new Error('Invalid response')
      }
      if (type === NAMESPACES.PREVIEW) {
        return data
      }
      await triggerDownload(data?.url, name)
    } catch (error) {
      const message = isAxiosConflictError(error)
        ? getAxiosErrorMessage(error, CONFLICT_DOWNLOAD_FALLBACK)
        : NAMESPACES.DOWNLOAD_ERROR
      handleToaster && handleToaster(message, 'error')
      logAxiosError(error, NAMESPACES.DOWNLOAD_ERROR)
    } finally {
      setIsDownloading(false)
    }
  }

  const handlePreviewFile = async (index: number, type?: string) => {
    if (!fileSubResourceData?.length || isDownloading) {
      return
    }
    const { path, name } = fileSubResourceData[index]
    setIsDownloading(true)
    try {
      const { data } = await fetchDownloadUrlForSingleFIles(
        path,
        name,
        documentId
      )
      if (!data) {
        throw new Error(NAMESPACE.INVALID_RESPONSE)
      }
      if (type === NAMESPACES.PREVIEW) {
        return data
      }
      setFaxUrl(data?.url)
      return data?.url
    } catch (error) {
      const message = isAxiosConflictError(error)
        ? getAxiosErrorMessage(error, CONFLICT_DOWNLOAD_FALLBACK)
        : NAMESPACES.DOWNLOAD_ERROR
      handleToaster && handleToaster(message, 'error')
      logAxiosError(error, NAMESPACES.DOWNLOAD_ERROR)
    } finally {
      setIsDownloading(false)
    }
  }
  const getAcceptedFiles = () => {
    const controlValues = element?.meta?.control_values
    const options = element?.meta?.options

    let types: string[] = []

    if (Array.isArray(controlValues)) {
      types = controlValues
    } else if (Array.isArray(options)) {
      types = options.map((opt: { label: string; value: string }) => opt.value)
    }

    const accept = types.map((type) => `.${type}`)

    if (types.includes('jpg')) {
      accept.push('.jpeg')
    }

    return accept.join(', ')
  }

  const incrementRefetchCounter = async () => {
    setFileSubResourceData([])
    let uploadData
    if (id && id !== NAMESPACES.NO_DATA) {
      uploadData = await fetchDataForViewAndEdit()
    } else if (documentId) {
      uploadData = await fetchDataForViewAndEdit(documentId)
    } else {
      setRefetchCounter((prev) => prev + 1)
      return
    }

    const hasScanFailed = uploadData?.attachment?.some(
      (file: FileDataFromExtraResource) =>
        file?.status === DOCUMENT_SECURITY_STATUS.ERROR_IN_SCAN
    )
    const rescanDocumentId = uploadData?.id ?? documentId
    if (hasScanFailed && rescanDocumentId) {
      try {
        await rescanDocumentApi(Number(rescanDocumentId))
      } catch (error) {
        console.error(error)
      }
    }
  }

  useEffect(() => {
    if (
      (mode == 'view' ||
        mode == 'edit' ||
        entityName ||
        (mode === 'add' && isDisabled)) &&
      !fileSubResourceData?.length &&
      !files?.length &&
      id &&
      id !== NAMESPACES.NO_DATA &&
      element.elementName === componentNames.FILE_UPLOAD
    ) {
      fetchDataForViewAndEdit()
    } else if (id === NAMESPACES.NO_DATA && isOpenForListing) {
      setNoDataFound(true)
    } else if (!id) {
      setFileSubResourceData([])
    }
  }, [id, entityName, refetctchCounter])

  const handleClearFileForEditMode = async (
    index: number,
    filePresent?: boolean
  ) => {
    try {
      if (!fromIntakeReview) {
        deleteFileAttachment(
          !filePresent
            ? {
                name: fileSubResourceData[index]?.name?.toString(),
                type: MULTI_UPLOAD_UTILS.ENTITY_NAME,
              }
            : {
                name: files[index]?.name?.toString(),
                type: MULTI_UPLOAD_UTILS.ENTITY_NAME,
              },
          documentId ?? 0
        )
      }
      const updatedFiles = !filePresent
        ? fileSubResourceData.filter((_, i) => i !== index)
        : files?.filter((_, i) => i !== index)
      !filePresent
        ? setFileSubResourceData(updatedFiles as FileDataFromExtraResource[])
        : setFiles(updatedFiles as File[])
      if (
        !updatedFiles?.length &&
        (!filePresent ? !files?.length : !fileSubResourceData?.length)
      ) {
        setNoDataFound(true)
        setValue &&
          setValue(
            element.keyName as Path<T>,
            {
              id: documentId ?? id,
              _state: STRINGS.REMOVED,
            } as PathValue<T, Path<T>>
          )
      }
    } catch (error) {
      console.log(error)
    }
  }
  const bulkDownload = async () => {
    if (!fileSubResourceData?.length || isDownloading) {
      return
    }

    setIsDownloading(true)
    try {
      const { data } = await fetchDownloadUrlForMultipleFIles(
        documentId ?? 0,
        NAMESPACES.ZIP_FILE
      )
      if (!data?.url || !data?.alias) {
        throw new Error('Invalid response')
      }
      await triggerDownload(data.url, data.alias)
    } catch (error) {
      const message = isAxiosConflictError(error)
        ? getAxiosErrorMessage(error, CONFLICT_DOWNLOAD_FALLBACK)
        : NAMESPACES.DOWNLOAD_ERROR
      handleToaster && handleToaster(message, 'error')
      logAxiosError(error, NAMESPACES.DOWNLOAD_ERROR)
    } finally {
      setIsDownloading(false)
    }
  }
  const triggerDownload = async (url: string, fileName: string) => {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(NAMESPACES.DOWNLOAD_ERROR)
    }

    const blob = await response.blob()
    const ext = getExtensionFromBlob(blob)
    if (!fileName.toLowerCase().endsWith(`.${ext}`)) {
      fileName += `.${ext}`
    }

    const blobUrl = window.URL.createObjectURL(blob)

    const aTag = document.createElement('a')
    aTag.href = blobUrl
    aTag.download = fileName
    document.body.appendChild(aTag)
    aTag.click()
    document.body.removeChild(aTag)
  }

  const splitFaxFunction = async (data: SplitObject[]) => {
    if (!selectedFile || !caseId) return
    try {
      dispatch(showLoader())
      const path = `${(
        fileSubResourceData[selectedFile.index]?.path || ''
      ).replace(/\/$/, '')}/${
        fileSubResourceData[selectedFile.index]?.name || ''
      }`
      await splitCaseDocumentApi(Number(caseId), path, data)
      refetchData()
      dispatch(
        addToaster({
          message: QUEUE_STRINGS.SPLIT_DOCUMENT_SUCCESS,
          type: 'success',
          id: uuid(),
        })
      )
      fetchDataForViewAndEdit()
    } catch (err) {
      console.error(err)
      dispatch(
        addToaster({
          message: QUEUE_STRINGS.SPLIT_DOCUMENT_ERROR,
          type: 'error',
          id: uuid(),
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }

  const getContentType = (index: number) => {
    let contentType =
      files[index]?.type || fileSubResourceData[index]?.content_type
    if (!contentType) {
      contentType =
        fileSubResourceData[index]?.name?.endsWith('.pdf') ||
        fileSubResourceData[index]?.alias?.endsWith('.pdf')
          ? 'application/pdf'
          : ''
    }
    return contentType
  }

  const handleFileSelected = async (index?: number) => {
    if (index === undefined) {
      setSelectedFile(undefined)
      return
    }
    const url =
      fileSubResourceData[index]?.status === DOCUMENT_SECURITY_STATUS.READY
        ? await handlePreviewFile(
            index,
            files[index]?.type || fileSubResourceData[index]?.type
          )
        : ''
    setSelectedFile({
      path:
        fileSubResourceData[index]?.path +
        '/' +
        fileSubResourceData[index]?.name,
      index,
      url,
      content_type: getContentType(index),
      status: fileSubResourceData[index]?.status,
    })
  }

  const onPopoutClick = () => {
    window.open(faxUrl, '_blank', 'noopener,noreferrer')
  }

  useEffect(() => {
    if (!fileSubResourceData.length) return

    const currentIndex = selectedFile?.index

    if (
      currentIndex !== undefined &&
      currentIndex < fileSubResourceData.length
    ) {
      return
    }

    handleFileSelected(0)
  }, [fileSubResourceData, selectedFile?.index])

  return (
    <div className="bg-[white]">
      <MultiSelectFileInput
        register={register}
        name={(EXTRA_FIELDS.EXTRA_FIELD + element.keyName) as Path<T>}
        errors={errors}
        label={element?.meta?.label}
        validation={
          getValidationsFromElement(element) as
            | RegisterOptions<T, Path<T>>
            | undefined
        }
        isRequired={(element?.meta?.required as boolean) || required}
        comment={`${
          STRINGS.MAX_SIZE + element?.meta?.max_value?.toString()
        } KB`}
        disabled={isDisabled}
        accept={getAcceptedFiles()}
        toolTip={element?.meta?.tooltip_text ?? ''}
        maxSizeOfFile={element?.meta?.max_value}
        minSizeOfFile={element?.meta?.min_value}
        handleToaster={handleToaster}
        mode={MODE_CONSTANTS.EDIT}
        onChangeValue={onChange}
        newData={filesUploaded}
        defaultData={fileSubResourceData}
        handleClearFileForEditMode={handleClearFileForEditMode}
        handleBulkDownload={bulkDownload}
        handleSingleDownload={handleDownloadSingleFile}
        isOpenForListing={isOpenForListing}
        setRenderMultiUpload={setRenderMultiUpload}
        noDataFound={noDataFound}
        tabIndex={tabIndex}
        files={files}
        setFiles={setFiles}
        isUploadDisabled={isUploadDisable}
        selectedFile={selectedFile}
        setSelectedFile={handleFileSelected}
        showSplitFax={true}
        renderSplitFax={() => (
          <SplitFax
            setIsSplitFax={(p) => p}
            faxUrl={selectedFile?.url || ''}
            splitFaxFunctionWithIdentification={splitFaxFunction}
            enableIdentification={true}
            showFaxSplitPanel={false}
            selectedFile={selectedFile}
            fromQueueTable={fromQueueTable}
          />
        )}
        aePcDocId={aePcDocId}
        docEntityId={docEntityId}
        fromQueueTable={fromQueueTable}
        customHeader={customHeader}
        onEditClick={onEditClick}
        onPopoutClick={onPopoutClick}
        isReadOnlyUser={isReadOnlyUser()}
        isEditButtonVisible={isEditButtonVisible}
        refreshDocumentsList={incrementRefetchCounter}
      />
    </div>
  )
}

export default MultiUploadWrapper
